// Deshacer (Ctrl+Z) y rehacer (Ctrl+Y o Ctrl+Shift+Z) en todos los campos de texto del sistema.
//
// Por qué hace falta: el deshacer del navegador guarda un historial por campo, pero lo pierde
// cuando el programa cambia el valor mientras se tipea. En este sistema muchos campos lo hacen
// (apellido y nombre en mayúsculas, valores con "$" y puntos, DNI limpio, descuento numérico…),
// así que ahí Ctrl+Z no hacía nada o hacía cosas raras.
//
// Cómo funciona: se instala UNA vez (index.js) escuchando a nivel documento, así vale para todas
// las ventanas sin tocar cada pantalla.
//  - Cada campo de texto lleva su propio historial mientras se escribe. Lo tipeado se agrupa
//    por palabras: un espacio o una pausa de más de PAUSA_MS empiezan un paso nuevo, así no se
//    deshace letra por letra.
//  - Ctrl+Z / Ctrl+Y vuelven el campo a un paso anterior / siguiente. El valor se carga como si
//    se tipeara (evento "input"), así pasa por el onChange de la pantalla y se respetan sus
//    reglas (mayúsculas, formato de pesos, etc.).
//  - Solo actúa en <input> de texto y <textarea>. El editor de Novedades (contenteditable) tiene
//    su propio deshacer y no se toca. Lo que el sistema completa solo (por ejemplo los datos del
//    paciente al cargar el DNI) no entra al historial como un paso tipeado, pero sí queda como
//    punto de partida: deshacer no lo borra.

export const PAUSA_MS = 700;

const TIPOS_DE_TEXTO = ['text', 'search', 'tel', 'email', 'url', 'number', ''];

export function esCampoDeTexto(el) {
  if (!el || el.disabled || el.readOnly) return false;
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  return TIPOS_DE_TEXTO.includes((el.getAttribute('type') || '').toLowerCase());
}

// Carga un valor en el campo como lo haría el teclado. Se usa el "setter" nativo y no
// el.value = …: React registra el último valor que vio, y si se asigna directo no se entera del
// cambio y no llama al onChange.
function cargarValor(el, valor) {
  const proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value').set;
  setter.call(el, valor);
  el.dispatchEvent(new Event('input', { bubbles: true }));
  try {
    // Cursor al final, que es donde lo espera quien deshace algo que estaba escribiendo.
    const fin = el.value.length;
    el.setSelectionRange(fin, fin);
  } catch (e) { /* algunos tipos (number, email) no admiten selección: no importa */ }
}

export function instalarDeshacerEnCampos(doc = document, ahora = () => Date.now()) {
  // Historial por campo. WeakMap: cuando el campo desaparece (se cierra la ventana), su historial
  // se va con él.
  const historiales = new WeakMap();
  let aplicando = false;

  const historialDe = (el) => {
    let h = historiales.get(el);
    if (!h) {
      h = { pasos: [el.value], indice: 0, ultimo: null };
      historiales.set(el, h);
    }
    return h;
  };

  // Si el valor cambió sin que se tipeara (el sistema lo completó o lo reformateó), ese valor
  // pasa a ser el paso actual, para no "deshacer" algo que la persona no hizo.
  const sincronizar = (el, h) => {
    if (h.pasos[h.indice] !== el.value) {
      h.pasos = h.pasos.slice(0, h.indice + 1);
      h.pasos.push(el.value);
      h.indice = h.pasos.length - 1;
      h.ultimo = null;
    }
  };

  const alEnfocar = (e) => {
    const el = e.target;
    if (!esCampoDeTexto(el)) return;
    sincronizar(el, historialDe(el));
  };

  const alEscribir = (e) => {
    const el = e.target;
    if (aplicando || !esCampoDeTexto(el)) return;
    const h = historialDe(el);
    // Se lee el valor un instante después: la pantalla puede transformarlo (mayúsculas, "$")
    // y lo que vale guardar es lo que quedó a la vista.
    setTimeout(() => {
      const valor = el.value;
      if (valor === h.pasos[h.indice]) return;
      const t = ahora();
      const seguido = h.ultimo !== null && t - h.ultimo < PAUSA_MS && h.indice > 0
        && !/\s$/.test(h.pasos[h.indice]);
      // Lo que estaba "adelante" (pasos deshechos) se descarta al escribir algo nuevo.
      h.pasos = h.pasos.slice(0, h.indice + 1);
      if (seguido) h.pasos[h.indice] = valor;
      else { h.pasos.push(valor); h.indice = h.pasos.length - 1; }
      h.ultimo = t;
    }, 0);
  };

  const mover = (el, paso) => {
    const h = historialDe(el);
    const destino = h.indice + paso;
    if (destino < 0 || destino >= h.pasos.length) return;
    h.indice = destino;
    h.ultimo = null; // lo próximo que se tipee empieza un paso nuevo
    aplicando = true;
    try { cargarValor(el, h.pasos[destino]); } finally { aplicando = false; }
    // Si la pantalla reformateó el valor cargado, el paso queda como se ve.
    setTimeout(() => { if (h.indice === destino) h.pasos[destino] = el.value; }, 0);
  };

  const alApretarTecla = (e) => {
    if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
    const el = e.target;
    if (!esCampoDeTexto(el)) return;
    const tecla = (e.key || '').toLowerCase();
    const deshacer = tecla === 'z' && !e.shiftKey;
    const rehacer = tecla === 'y' || (tecla === 'z' && e.shiftKey);
    if (!deshacer && !rehacer) return;
    // En los campos de texto manda este historial: el del navegador se anula siempre, así no
    // se pisan entre los dos.
    e.preventDefault();
    const h = historialDe(el);
    if (deshacer) {
      sincronizar(el, h);
      mover(el, -1);
    } else {
      mover(el, 1);
    }
  };

  doc.addEventListener('focusin', alEnfocar, true);
  doc.addEventListener('input', alEscribir, true);
  doc.addEventListener('keydown', alApretarTecla, true);
  return () => {
    doc.removeEventListener('focusin', alEnfocar, true);
    doc.removeEventListener('input', alEscribir, true);
    doc.removeEventListener('keydown', alApretarTecla, true);
  };
}
